/** Selection controls: search, filters, month/date/range, category, currency, amount. */
import { useId } from 'react'
import type { Account, CategoryId, Money } from '@/generated/types'
import categorySchema from '@/generated/product/data/category.schema.json'
import { t, type CopyKey } from '@/lib/copy'
import { formatMonth, formatMoney, toMajorUnits } from '@/lib/format'
import { categoryName } from '@/lib/display'
import { Chip } from './primitives'
import { IconSearch } from './icons'

export const CATEGORY_IDS = categorySchema.$defs.categoryId.enum as CategoryId[]

export function SearchField({
  value,
  onChange,
  placeholder,
}: {
  value: string
  onChange: (next: string) => void
  placeholder: string
}) {
  return (
    <div className="search-field">
      <span className="search-icon">
        <IconSearch />
      </span>
      <input
        type="search"
        className="input"
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

export interface FilterOption<T extends string> {
  value: T
  labelKey: CopyKey
}

export function FilterBar<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  options: FilterOption<T>[]
  value: T
  onChange: (next: T) => void
  label: string
}) {
  return (
    <div className="filter-bar" role="group" aria-label={label}>
      {options.map((option) => (
        <Chip
          key={option.value}
          label={t(option.labelKey)}
          active={option.value === value}
          onClick={() => onChange(option.value)}
        />
      ))}
    </div>
  )
}

export function MonthPicker({
  months,
  value,
  onChange,
}: {
  months: string[]
  value: string
  onChange: (next: string) => void
}) {
  const id = useId()
  return (
    <div className="field">
      <label className="visually-hidden" htmlFor={id}>
        {t('categories.title')}
      </label>
      <select id={id} className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        {months.map((month) => (
          <option key={month} value={month}>
            {formatMonth(month)}
          </option>
        ))}
      </select>
    </div>
  )
}

export function DateRangePicker({
  from,
  to,
  onChange,
}: {
  from: string
  to: string
  onChange: (next: { from: string; to: string }) => void
}) {
  const fromId = useId()
  const toId = useId()
  return (
    <div className="row" aria-label={t('transactions.filter.dateRange')}>
      <label className="visually-hidden" htmlFor={fromId}>
        {t('transactions.filter.dateRange')}
      </label>
      <input
        id={fromId}
        type="date"
        className="input"
        value={from}
        max={to || undefined}
        onChange={(e) => onChange({ from: e.target.value, to })}
      />
      <label className="visually-hidden" htmlFor={toId}>
        {t('transactions.filter.dateRange')}
      </label>
      <input
        id={toId}
        type="date"
        className="input"
        value={to}
        min={from || undefined}
        onChange={(e) => onChange({ from, to: e.target.value })}
      />
    </div>
  )
}

export function CategoryPicker({
  value,
  onChange,
  label,
}: {
  value: CategoryId
  onChange: (next: CategoryId) => void
  label: string
}) {
  const id = useId()
  return (
    <div className="field">
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value as CategoryId)}
      >
        {CATEGORY_IDS.map((category) => (
          <option key={category} value={category}>
            {categoryName(category)}
          </option>
        ))}
      </select>
    </div>
  )
}

export function AccountPicker({
  accounts,
  value,
  onChange,
  label,
  includeNew,
}: {
  accounts: Account[]
  value: string
  onChange: (next: string) => void
  label: string
  includeNew?: boolean
}) {
  const id = useId()
  return (
    <div className="field">
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <select id={id} className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        {includeNew ? <option value="">{t('importReview.account.new')}</option> : null}
        {accounts.map((account) => (
          <option key={account.id} value={account.id}>
            {account.name}
            {account.mask ? ` ••${account.mask}` : ''}
          </option>
        ))}
      </select>
    </div>
  )
}

/** ISO 4217 codes the app can display. The list stays short on purpose. */
const CURRENCIES = ['USD', 'CAD', 'EUR', 'GBP', 'AUD', 'JPY', 'CHF', 'SEK', 'NZD']

export function CurrencyPicker({
  value,
  onChange,
  label,
}: {
  value: string
  onChange: (next: string) => void
  label: string
}) {
  const id = useId()
  return (
    <div className="field">
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <select id={id} className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        {CURRENCIES.map((code) => (
          <option key={code} value={code}>
            {code}
          </option>
        ))}
      </select>
    </div>
  )
}

/** Edits a Money value in major units while keeping minor units on the wire. */
export function AmountStepper({
  value,
  onChange,
  label,
  step = 25,
  min = 0,
}: {
  value: Money
  onChange: (next: Money) => void
  label: string
  step?: number
  min?: number
}) {
  const id = useId()
  const major = toMajorUnits(value)
  const scale = value.amount === 0 ? 100 : Math.round(value.amount / major) || 100

  const set = (nextMajor: number) =>
    onChange({ amount: Math.max(min, Math.round(nextMajor * scale)), currency: value.currency })

  return (
    <div className="field">
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <div className="row">
        <button type="button" className="btn btn-secondary" onClick={() => set(major - step)} aria-label={`${label} −${step}`}>
          −
        </button>
        <input
          id={id}
          className="input numeric"
          type="number"
          inputMode="decimal"
          value={Number.isFinite(major) ? major : 0}
          min={min}
          step={step}
          onChange={(e) => set(Number(e.target.value))}
          style={{ textAlign: 'center' }}
        />
        <button type="button" className="btn btn-secondary" onClick={() => set(major + step)} aria-label={`${label} +${step}`}>
          +
        </button>
      </div>
      <span className="caption numeric">{formatMoney(value)}</span>
    </div>
  )
}
